import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { NodeProps, Node } from "@xyflow/react";
import {
  nodeAtomFamily,
  nodesAtom,
  aiSuggestedEditsAtomFamily,
  aiSuggestedEditsLoadingAtomFamily,
  zslExecutionErrorAtomFamily,
  aiSuggestionErrorAtomFamily,
} from "../nodes";
import { ZslCreator } from "@zapier/service-providers";
import { canvasAtom } from "../canvas";
import { edgesAtom } from "../edges";
import { emitAiPredictionOutputEventSetter } from "../events";
import { DefaultNodeData } from "../types";

// Keep track of the current abort controller by node id
export const abortControllerAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom<AbortController | null>(null);
  baseAtom.debugLabel = `abortControllerAtomFamily(${nodeId})`;
  return baseAtom;
});

// Abort current stream by node id
export const abortCurrentStreamSetterAtomFamily = atomFamily(
  (nodeId: string) => {
    const baseAtom = atom(null, (get, set) => {
      const currentAbortController = get(abortControllerAtomFamily(nodeId));
      if (currentAbortController) {
        currentAbortController.abort();
        set(abortControllerAtomFamily(nodeId), null);
        set(aiSuggestedEditsLoadingAtomFamily(nodeId), false);
        set(aiSuggestedEditsAtomFamily(nodeId), null);
      }
    });

    baseAtom.debugLabel = `abortCurrentStreamSetterAtomFamily(${nodeId})`;
    return baseAtom;
  },
);

export const getEditsSetter = atom(
  null,
  async (get, set, { sourceNodeId }: { sourceNodeId: NodeProps["id"] }) => {
    const sourceNode = get(
      nodeAtomFamily(sourceNodeId),
    ) as Node<DefaultNodeData>;
    if (!sourceNode) {
      return;
    }

    function clearAbortController() {
      set(abortControllerAtomFamily(sourceNodeId), null);
    }

    // If there's an ongoing stream, abort it
    const currentAbortController = get(abortControllerAtomFamily(sourceNodeId));
    if (currentAbortController) {
      currentAbortController.abort();
    }

    // Create new abort controller for this stream
    set(abortControllerAtomFamily(sourceNodeId), new AbortController());

    set(aiSuggestedEditsLoadingAtomFamily(sourceNodeId), true); // Set loading to true at start
    set(aiSuggestedEditsAtomFamily(sourceNodeId), null); // Clear any previous suggestion
    set(zslExecutionErrorAtomFamily(sourceNodeId), null); // Clear any previous error

    const nodes = get(nodesAtom);
    const edges = get(edgesAtom);
    const canvas = get(canvasAtom);
    const connectedSourceNodeId =
      edges.find((edge) => edge.target === sourceNodeId)?.source || undefined;
    //filter out the source node from the nodes array
    const filteredNodes = nodes.filter((node) => node.id !== sourceNodeId);
    //take the source node label and make it the prompt
    const userPrompt = sourceNode.data.label;

    try {
      // Start streaming updates with abort signal
      await ZslCreator.streamCanvasEdit(
        {
          prompt: userPrompt,
          sourceNodeId: connectedSourceNodeId,
          canvas: { ...canvas, nodes: filteredNodes, edges },
        },
        {
          onPartial: (data) => {
            set(aiSuggestedEditsAtomFamily(sourceNodeId), data);
          },
          onComplete: (data) => {
            set(aiSuggestedEditsAtomFamily(sourceNodeId), data);
            // Emit prediction output event with actual output data
            set(emitAiPredictionOutputEventSetter, {
              prediction_id: data.additions[0].id,
              prediction_source: "canvas.suggestion",
              prediction_json_input: JSON.stringify({
                prompt: userPrompt,
                canvas: { ...canvas, nodes: filteredNodes, edges },
                sourceNodeId,
              }),
              prediction_json_output: JSON.stringify(data),
              correlation_id: null,
            });
            // Clear the abort controller on completion
            clearAbortController();
          },
          onError: (error) => {
            const errorMessage =
              error.error || "An error occurred while generating suggestions";
            // Set the suggestion error in the atom family
            set(aiSuggestionErrorAtomFamily(sourceNodeId), {
              message: errorMessage,
              error: error,
            });
            // Clear the abort controller on error
            clearAbortController();
          },
        },
        currentAbortController?.signal,
      );
    } catch (error) {
      console.error("Failed to update node:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred while generating suggestions";

      // Set the suggestion error in the atom family
      set(aiSuggestionErrorAtomFamily(sourceNodeId), {
        message: errorMessage,
        error,
      });
    } finally {
      set(aiSuggestedEditsLoadingAtomFamily(sourceNodeId), false); // Set loading to false when done
      // Clear the abort controller in finally block to ensure it's always cleaned up
      clearAbortController();
    }
  },
);

getEditsSetter.debugLabel = "getEditsSetter";
